/**
 * CrossAppSyncPanel — Account-page section for Attuned members.
 *
 * Lets the user pick which profile pieces sync across Fryqnc / Pulsynq /
 * MindLynq. Non-Attuned members see a locked preview with a CTA to upgrade.
 */
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useCrossAppSync, type CrossAppSyncPrefs } from "@/lib/crossApp/useCrossAppSync";
import { toast } from "sonner";

type Row = { key: keyof CrossAppSyncPrefs; label: string; hint: string };

const ROWS: Row[] = [
  { key: "sync_photos",  label: "Photos",        hint: "Same gallery in all three apps." },
  { key: "sync_bio",     label: "Bio",           hint: "One bio everywhere." },
  { key: "sync_prompts", label: "Prompts",       hint: "Reuse your prompt answers." },
  { key: "sync_voice",   label: "Voice intro",   hint: "Single voice intro across communities." },
  { key: "sync_matches", label: "Match history", hint: "Carry over likes and passes." },
];

interface Props {
  isAttuned: boolean;
}

const CrossAppSyncPanel = ({ isAttuned }: Props) => {
  const { prefs, loading, update } = useCrossAppSync();

  const onToggle = async (key: keyof CrossAppSyncPrefs, value: boolean) => {
    const { error } = await update({ [key]: value });
    if (error) toast.error(error);
  };

  return (
    <section className="mt-6 rounded-2xl border border-border p-6 bg-card space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">All-Access sync</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose what travels with you across Fryqnc, Pulsynq, and MindLynq.
          </p>
        </div>
        {!isAttuned && (
          <Link
            to="/pricing"
            className="text-xs font-medium underline hover:text-foreground"
          >
            Upgrade to Attuned
          </Link>
        )}
      </div>

      <div className={`grid gap-4 ${!isAttuned ? "opacity-60 pointer-events-none" : ""}`}>
        {ROWS.map((row) => (
          <div
            key={row.key}
            className="flex items-start justify-between gap-4 rounded-xl border border-border/60 bg-background/40 px-4 py-3"
          >
            <div className="min-w-0">
              <Label htmlFor={`sync-${row.key}`} className="text-sm font-medium">
                {row.label}
              </Label>
              <p className="mt-0.5 text-xs text-muted-foreground">{row.hint}</p>
            </div>
            <Switch
              id={`sync-${row.key}`}
              checked={prefs[row.key]}
              disabled={!isAttuned || loading}
              onCheckedChange={(v) => onToggle(row.key, v)}
            />
          </div>
        ))}
      </div>

      {!isAttuned && (
        <p className="text-[11px] text-muted-foreground">
          Locked — Attuned members only.
        </p>
      )}
    </section>
  );
};

export default CrossAppSyncPanel;
